// Custom hooks logic based on https://tkdodo.eu/blog/working-with-zustand

import { create } from 'zustand'
import { createJSONStorage, devtools, persist } from 'zustand/middleware'

import type { ApiAddressSuggestion } from '@/types/address-suggestions'

type State = {
  deliveryAddress?: ApiAddressSuggestion
  billingAddress?: ApiAddressSuggestion
}

type Actions = {
  setDeliveryAddress: (address?: ApiAddressSuggestion) => void
  setBillingAddress: (address?: ApiAddressSuggestion) => void
  reset: () => void
}

type AddressStore = State & Actions

const initialState: State = {
  deliveryAddress: undefined,
  billingAddress: undefined,
}

// ⬇️ not exported, so that no one can subscribe to the entire store
const useAddressStore = create<AddressStore>()(
  devtools(
    persist(
      (set) => ({
        ...initialState,
        setDeliveryAddress: (address) => set({ deliveryAddress: address }),
        setBillingAddress: (address) => set({ billingAddress: address }),
        reset: () => set(initialState),
      }),
      {
        name: 'address-store',
        storage: createJSONStorage(() => sessionStorage),
      },
    ),
  ),
)

// 💡 exported - consumers don't need to write selectors
export const useDeliveryAddress = () => useAddressStore((state: AddressStore) => state.deliveryAddress)
export const useBillingAddress = () => useAddressStore((state: AddressStore) => state.billingAddress)

// 🎉 one selector for all our actions
export const useAddressStoreActions = () =>
  useAddressStore((state: AddressStore) => ({
    setDeliveryAddress: state.setDeliveryAddress,
    setBillingAddress: state.setBillingAddress,
    reset: state.reset,
  }))
